import {match} from 'ts-pattern'
import {z} from 'zod'
import {zx} from '../type'
import {
  zResolutionCfg,
  zQuestionForecastType,
  zBounds,
  zResolutionsStandalone,
  zQuestionCfgShape,
  upgradeBoundsVersion,
  upgradeComparisonKeys
} from './question'
import type {QuestionForecastType, ResolutionCfg} from './question'
import {mapValues, pick, pipe} from 'remeda'
export const subqTypes = ['cause', 'related', 'consequence'] as const
export const zSubqType = z.enum(subqTypes)
export type SubqType = (typeof subqTypes)[number]
export const subqStatus = ['draft', 'proposed', 'approved', 'paused', 'resolved', 'closed'] as const
export type SubqStatus = (typeof subqStatus)[number]

export const zQuestionCommonCfg = z.object(
  pick(zQuestionCfgShape, [
    'title',
    'subTitle',
    'description',
    'amountType',
    'unitLabel',
    'unitLabelLong',
    'numberFormat',
    'forecastType',
    'defaultBounds',
    'hardBounds',
    'subquestionOrder',
    'comparisons',
    'boundsVersion'
  ])
)
export const zResolutionValueTypes = {
  standard: zResolutionCfg,
  override: zResolutionCfg
    .pick({position: true})
    .partial()
    .extend({type: z.literal('override'), enableForecasts: zx.nullish.bool})
}

const zResolutionsOverride = z.record(
  zQuestionForecastType,
  z.record(z.discriminatedUnion('type', [zResolutionValueTypes.standard, zResolutionValueTypes.override], {}))
)

export const zSubqStatus = z.enum(subqStatus).default('draft')
export const zSubqCommonCfg = zQuestionCommonCfg.extend({
  status: zSubqStatus,
  forecastType: zQuestionForecastType.default('likelihood'),
  version: z.number().default(2),

  /** the main resolution config if this is a likelihood question */
  likelihood: zResolutionCfg.omit({deleted: true}).default({
    enableForecasts: false,
    label: 'Likelihood'
  }),
  /** the main resolution config if this is an amount question */
  amount: zResolutionCfg.omit({deleted: true}).default({
    enableForecasts: false,
    label: 'Quantity'
  }),
  /** if true, allows subq's as children of this one */
  childrenEnabled: z.boolean().default(false),
  consequenceResolutions: zResolutionsStandalone.default({})
})

const zSubqTypes = {
  Cause: zSubqCommonCfg.extend({
    subquestionType: z.literal('cause'),
    resolutions: zResolutionsStandalone.default({}),
    defaultBounds: zBounds.optional()
  }),
  Consequence: zSubqCommonCfg.extend({
    subquestionType: z.literal('consequence'),
    resolutions: zResolutionsOverride.default({})
  }),
  Related: zSubqCommonCfg.extend({
    subquestionType: z.literal('related'),
    resolutions: zResolutionsStandalone.default({})
  })
}

export const subqMd = {
  cause: {
    desc: "A question whose resolution impacts the main question. Forecast the cause, and also the main question conditional on the cause's resolution"
  },
  consequence: {
    desc: 'A question which depends on how the main question is resolved. Forecast the consequence conditional on the questions resolution'
  },
  related: {
    desc: 'A plain question, i.e. not a conditional forecast'
  }
} satisfies Record<SubqType, {desc: string}>

export const zSubqCfg = z.preprocess(
  // add default resolution types. We used to look at the version, but its safer to just derive if it makes sense for the current version
  q =>
    pipe(
      match(q)
        .with({subquestionType: 'consequence', resolutions: {}}, q => ({
          ...q,
          resolutions: mapValues(z.record(z.record(z.object({}).passthrough())).parse(q.resolutions ?? {}), rs =>
            mapValues(rs, r => ({type: 'standard', ...r}))
          )
        }))
        .otherwise(() => q),
      upgradeBoundsVersion,
      upgradeComparisonKeys
    ),
  z.discriminatedUnion('subquestionType', [zSubqTypes.Cause, zSubqTypes.Consequence, zSubqTypes.Related])
)

/** usually we rely on default in zod, however for some things we want to keep things as they are and have a default for new */
export const subqCfgForNew = {
  subquestionType: 'related' as SubqType,
  boundsVersion: '2',
  status: 'proposed' as SubqStatus,
  likelihood: {enableForecasts: true, label: 'Likelihood'},
  amount: {enableForecasts: true, label: ''}
}

export const zSubqFetched = z.object({
  id: z.string(),
  question_id: z.string(),
  parent_id: zx.nullish.string,
  cfg: zSubqCfg,
  deleted: zx.nullishToUndefined(z.boolean().optional()),
  created_at: zx.nullish.string
})
export type SubqFetched = z.infer<typeof zSubqFetched>
export type SubqCfg = z.infer<typeof zSubqCfg>
export type SubqCfgWithId = SubqCfg & {id: string}
export type SubqCfgCause = z.infer<typeof zSubqTypes.Cause>
export type SubqCfgConsequence = z.infer<typeof zSubqTypes.Consequence>
export type SubqCfgRelated = z.infer<typeof zSubqTypes.Related>
/** COnfig that subquestions and questions share */
export type QuestionCfgCommon = z.infer<typeof zQuestionCommonCfg>
export type Resolutions = z.infer<typeof zResolutionsStandalone>

export type ResolutionValueStandard = z.infer<typeof zResolutionValueTypes.standard>
export type ResolutionValueOverride = z.infer<typeof zResolutionValueTypes.override>
export type ResolutionValueAny = ResolutionCfg | ResolutionValueStandard | ResolutionValueOverride

export type ResolutionKeyDependents = {
  subquestionType: SubqType
  questionForecastType: QuestionForecastType
  subquestionForecastType: QuestionForecastType
}
